<template>
  <DataTable :value="rows" striped-rows class="custom-table procedures-table">
    <Column
      field="requestDate"
      :header="t('wkt.client.procedures.table.headers.date')"
      sortable
      headerStyle="width: 9rem"
      bodyStyle="width: 9rem"
    >
      <template #body="{ data }">
        {{ data.requestDate ? formatLongDateTime(data.requestDate, true) : "---" }}
      </template>
    </Column>

    <Column
      field="firstname"
      :header="t('wkt.client.procedures.table.headers.name')"
      sortable
      headerStyle="width: 12rem"
      bodyStyle="width: 12rem"
    >
      <template #body="{ data }">
        <button
          type="button"
          class="procedures-table__employee"
          @click="$emit('employee', data.employeeId)"
        >
          {{ employeeName(data) }}
        </button>
      </template>
    </Column>

    <Column
      field="requestType"
      :header="t('wkt.client.procedures.table.headers.type')"
      sortable
      headerStyle="width: 12rem"
      bodyStyle="width: 12rem"
    >
      <template #body="{ data }">
        <div class="procedures-table__type">
          <span>{{ definitionByRequest(data)?.name || "---" }}</span>
          <span v-if="data.adminId" class="procedures-table__muted">
            {{ t("wkt.client.procedures.created_by_admin") }}
          </span>
        </div>
      </template>
    </Column>

    <Column
      field="requestTitle"
      :header="t('wkt.client.procedures.table.headers.request')"
      sortable
      headerStyle="width: 15rem"
      bodyStyle="width: 15rem"
    >
      <template #body="{ data }">
        <div class="procedures-table__request">
          <strong>{{ data.requestTitle || "---" }}</strong>
          <span v-if="projectLabel(data)" class="procedures-table__muted">
            {{ projectLabel(data) }}
          </span>
        </div>
      </template>
    </Column>

    <Column
      :header="t('wkt.client.procedures.table.headers.attached')"
      headerStyle="width: 16rem"
      bodyStyle="width: 16rem"
    >
      <template #body="{ data }">
        <div class="procedures-table__attached">
          <span
            v-for="(line, index) in requestDetails(data)"
            :key="`${data.id}-${index}`"
          >
            {{ line }}
          </span>
          <button
            v-for="document in data.documents || []"
            :key="`${data.id}-${document.id}`"
            type="button"
            class="procedures-table__document"
            @click="downloadDocument(data.id, document.id, document.documentName)"
          >
            {{ document.documentName || "---" }}
          </button>
        </div>
      </template>
    </Column>

    <Column
      v-if="mode !== 'history'"
      headerStyle="width: 10rem"
      bodyStyle="width: 10rem"
    >
      <template #body="{ data }">
        <Button
          v-if="secondaryLinkLabel(data)"
          class="footer-primary-button procedures-table__action-button"
          text
          size="small"
          :label="secondaryLinkLabel(data).toUpperCase()"
          @click="handleSecondaryAction(data)"
        />
      </template>
    </Column>

    <Column
      v-if="mode === 'history'"
      :header="t('wkt.client.procedures.table.headers.status')"
      headerStyle="width: 9rem"
      bodyStyle="width: 9rem"
    >
      <template #body="{ data }">
        <div class="procedures-table__status">
          <Tag :severity="statusSeverity(data.requestStatus)" :value="statusLabel(data.requestStatus)" />
        </div>
      </template>
    </Column>

    <Column
      v-if="mode === 'history'"
      field="approverComment"
      :header="t('wkt.client.procedures.table.headers.comments')"
      headerStyle="width: 14rem"
      bodyStyle="width: 14rem"
    >
      <template #body="{ data }">
        <div class="procedures-table__comment-cell">
          {{ data.approverComment || "---" }}
        </div>
      </template>
    </Column>

    <Column headerStyle="width: 11rem" bodyStyle="width: 11rem">
      <template #body="{ data }">
        <div class="procedures-table__action-stack">
          <Button
            v-if="mode === 'approver'"
            class="footer-primary-button"
            text
            size="small"
            :label="t('wkt.client.common.dialog-buttons.approve')"
            @click="$emit('approve', data.id)"
          />
          <Button
            v-if="mode === 'approver'"
            class="footer-button-cancel"
            text
            size="small"
            :label="t('wkt.client.common.dialog-buttons.decline')"
            @click="$emit('deny', data.id)"
          />
          <Button
            v-if="mode === 'own-pending'"
            class="footer-primary-button"
            text
            size="small"
            :label="t('wkt.client.common.dialog-buttons.edit')"
            @click="$emit('edit', data.id)"
          />
          <Button
            v-if="mode === 'history'"
            class="footer-primary-button"
            text
            size="small"
            :label="t('wkt.client.procedures.procedure-card.show')"
            @click="$emit('show', data.id)"
          />
        </div>
      </template>
    </Column>
  </DataTable>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { useDateFormatter } from "@wkt/composables/useDateFormatter";
import { gwtApi } from "@wkt/api/gwt/gwtAPI";
import {
  ProcedureDefinitionClass,
  ProcedureRequestStatus,
  type FullProcedureDefinitionDTO,
  type FullProcedureRequestDTO,
} from "@wkt/api/procedures";

const props = defineProps<{
  rows: FullProcedureRequestDTO[];
  definitions: Record<number, FullProcedureDefinitionDTO>;
  mode: "own-pending" | "history" | "approver";
}>();

const emit = defineEmits<{
  edit: [requestId: number];
  show: [requestId: number];
  approve: [requestId: number];
  deny: [requestId: number];
  employee: [employeeId?: number | null];
  ownCalendar: [];
  approverCalendar: [requestId: number];
  expense: [expenseSheetId: number];
}>();

const { t } = useI18n();
const { formatLongDateTime, formatSimpleDate } = useDateFormatter();
const sessionInformation = gwtApi.getSessionInformation();
const enabledDefinitions = (((sessionInformation as any)?.procedures || []) as FullProcedureDefinitionDTO[]);

function definitionByRequest(request: FullProcedureRequestDTO): FullProcedureDefinitionDTO | undefined {
  const requestType = request.requestType;
  return requestType == null ? undefined : props.definitions[requestType];
}

function activeDefinitionForRequest(request: FullProcedureRequestDTO): FullProcedureDefinitionDTO | undefined {
  const requestType = request.requestType;
  if (requestType == null) {
    return undefined;
  }

  return enabledDefinitions.find((definition) => definition.id === requestType) || definitionByRequest(request);
}

function employeeName(request: FullProcedureRequestDTO): string {
  const firstname = request.firstname?.trim() || "";
  const lastname = request.lastname?.trim() || "";
  return `${firstname} ${lastname}`.trim() || "---";
}

function projectLabel(request: FullProcedureRequestDTO): string {
  if (!request.projectId) {
    return "";
  }

  const shortName = request.projectShortName?.trim();
  const description = request.projectDescription?.trim();

  if (shortName && description) {
    return `${shortName} - ${description}`;
  }

  return shortName || description || "---";
}

function requestDetails(request: FullProcedureRequestDTO): string[] {
  const definition = activeDefinitionForRequest(request);
  const lines: string[] = [];

  if (request.fromDate && (definition?.showFromDate ?? true)) {
    lines.push(
      `${definition?.fromDateLabel || t("wkt.client.procedures.procedure-card.start_date")}: ${formatSimpleDate(request.fromDate)}`
    );
  }

  if (request.toDate && (definition?.showToDate ?? true)) {
    lines.push(
      `${definition?.toDateLabel || t("wkt.client.procedures.procedure-card.end_date")}: ${formatSimpleDate(request.toDate)}`
    );
  }

  if (request.quantity != null && (definition?.showQuantity ?? true)) {
    lines.push(
      `${definition?.quantityLabel || t("wkt.client.procedures.edit-window.quantity_placeholder")}: ${request.quantity}`
    );
  }

  return lines;
}

function statusLabel(requestStatus?: number | null): string {
  switch (requestStatus) {
    case ProcedureRequestStatus.PREPARING:
      return t("wkt.client.procedures.procedure-card.preparing_status");
    case ProcedureRequestStatus.PENDING:
      return t("wkt.client.procedures.procedure-card.pending_status");
    case ProcedureRequestStatus.APPROVED:
      return t("wkt.client.procedures.procedure-card.approved_status");
    case ProcedureRequestStatus.DECLINED:
      return t("wkt.client.procedures.procedure-card.declined_status");
    case ProcedureRequestStatus.CANCELED:
      return t("wkt.client.procedures.procedure-card.canceled_status");
    case ProcedureRequestStatus.PROCESSED:
      return t("wkt.client.procedures.procedure-card.completed_status");
    default:
      return "---";
  }
}

function statusSeverity(requestStatus?: number | null): string {
  switch (requestStatus) {
    case ProcedureRequestStatus.PENDING:
      return "warn";
    case ProcedureRequestStatus.APPROVED:
      return "success";
    case ProcedureRequestStatus.DECLINED:
      return "danger";
    case ProcedureRequestStatus.PROCESSED:
      return "info";
    default:
      return "secondary";
  }
}

function secondaryLinkLabel(request: FullProcedureRequestDTO): string {
  if (request.requestStatus !== ProcedureRequestStatus.PENDING) {
    return "";
  }

  if (request.expenseSheetId) {
    return t("wkt.client.procedures.procedure-card.show_expense_sheet");
  }

  if (activeDefinitionForRequest(request)?.procedureClass === ProcedureDefinitionClass.VACATION_LEAVE) {
    return props.mode === "approver"
      ? t("wkt.client.procedures.procedure-card.show_calendar")
      : t("wkt.client.procedures.procedure-card.show_my_calendar");
  }

  return "";
}

function handleSecondaryAction(request: FullProcedureRequestDTO): void {
  if (request.expenseSheetId) {
    emit("expense", request.expenseSheetId);
    return;
  }

  if (props.mode === "approver") {
    emit("approverCalendar", request.id);
    return;
  }

  emit("ownCalendar");
}

function downloadDocument(
  requestId?: number,
  documentId?: number,
  documentName?: string | null
): void {
  if (!requestId || !documentId) {
    return;
  }

  gwtApi.downloadFile(
    `user/getRequestDocument/${requestId}/${documentId}`,
    documentName || "document"
  );
}
</script>

<style scoped>
.procedures-table:deep(.p-datatable-table-container) {
  height: auto !important;
}

.procedures-table:deep(.p-datatable-table) {
  width: 100%;
  table-layout: fixed;
}

.procedures-table:deep(.p-datatable-thead > tr > th) {
  padding: 0.7rem 1rem;
  vertical-align: middle;
  text-align: left;
}

.procedures-table:deep(.p-datatable-tbody > tr > td) {
  padding: 0.7rem 1rem;
  vertical-align: top;
  text-align: left;
}

.procedures-table__employee,
.procedures-table__document,
.procedures-table__link {
  border: none;
  background: transparent;
  padding: 0;
  color: var(--primary-color);
  cursor: pointer;
  font: inherit;
  text-align: left;
}

.procedures-table__employee:hover,
.procedures-table__document:hover,
.procedures-table__link:hover {
  text-decoration: underline;
}

.procedures-table__type,
.procedures-table__request,
.procedures-table__attached,
.procedures-table__status,
.procedures-table__comment-cell,
.procedures-table__action-stack {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.procedures-table__muted,
.procedures-table__comment {
  color: var(--grey-text);
  font-size: 0.85rem;
}

.procedures-table__status :deep(.p-tag) {
  padding-inline: 0;
  align-self: flex-start;
}

.procedures-table__action-stack {
  align-items: flex-start;
}

.procedures-table__action-button:deep(.p-button-label) {
  white-space: normal;
  text-align: left;
}

.procedures-table__action-button {
  justify-content: flex-start;
  padding-inline: 0;
}

.procedures-table__attached {
  white-space: normal;
}

.procedures-table__comment-cell {
  white-space: normal;
  color: var(--grey-text);
  font-size: 0.85rem;
}

.procedures-table__employee,
.procedures-table__document,
.procedures-table__link,
.procedures-table strong,
.procedures-table span {
  line-height: 1.3;
}
</style>
